import { BasePrinterExecutor } from './BaseExecutor';

const hasWebBluetoothSupport = () =>
  typeof navigator !== 'undefined' && !!navigator.bluetooth;

const DEFAULT_OPTIONAL_SERVICES = [
  0xffe0,
  0xff02,
  0xffd0,
  0xffb0,
  'device_information',
];

const SERVICE_CANDIDATES = [0xffe0, 0xff02, 0xffd0, 0xffb0];
const CHARACTERISTIC_CANDIDATES = [0xffe1, 0xff01, 0xffd1, 0xffb2];
const BLE_WRITE_CHUNK_SIZE = 160;

const wait = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export class WebBluetoothPrinterExecutor extends BasePrinterExecutor {
  constructor() {
    super({ type: 'web-bluetooth', environment: 'web' });
    this.device = null;
    this.server = null;
    this.outputCharacteristic = null;
    this.selectedServiceUuid = null;
    this.selectedCharacteristicUuid = null;
    this.handleDisconnected = this.handleDisconnected.bind(this);
  }

  getCapabilities() {
    const supported = hasWebBluetoothSupport();
    return {
      canDiscover: supported,
      canPair: supported,
      canPrint: supported,
      transports: supported ? ['bluetooth'] : [],
      persistentConnection: false,
      notes: supported
        ? [
            'Supports BLE printers that expose writable characteristics (e.g. UUID FF01/FFE1).',
            'Requires a user gesture to initiate device discovery.',
          ]
        : ['Web Bluetooth API is not available in this browser.'],
    };
  }

  createDescriptor(device) {
    if (!device) return null;
    return {
      id: device.id,
      name: device.name || 'Bluetooth Printer',
      transport: 'bluetooth',
      address: device.id,
      vendorId: device.vendorID || null,
      productId: device.productID || null,
      meta: {
        uuids: Array.isArray(device.uuids) ? device.uuids.slice() : [],
      },
      bluetoothDevice: device,
    };
  }

  async discoverDevices(options = {}) {
    if (!hasWebBluetoothSupport()) {
      return {
        success: false,
        devices: [],
        message: 'Web Bluetooth API is not available in this browser.',
      };
    }

    const requestOptions = {};
    if (Array.isArray(options.filters) && options.filters.length > 0) {
      requestOptions.filters = options.filters;
    } else if (options.acceptAllDevices !== false) {
      requestOptions.acceptAllDevices = true;
    } else {
      // If acceptAllDevices is explicitly false and no filters provided, fall back to accept all.
      requestOptions.acceptAllDevices = true;
    }

    requestOptions.optionalServices =
      options.optionalServices || DEFAULT_OPTIONAL_SERVICES;

    try {
      const device = await navigator.bluetooth.requestDevice(requestOptions);
      if (!device) {
        return {
          success: false,
          devices: [],
          message: 'No printer selected.',
        };
      }
      this.device = device;
      return {
        success: true,
        devices: [this.createDescriptor(device)],
      };
    } catch (error) {
      if (error?.name === 'NotFoundError') {
        return {
          success: false,
          devices: [],
          message: 'No device selected.',
        };
      }
      return {
        success: false,
        devices: [],
        message: error.message || 'Failed to discover devices.',
        error,
      };
    }
  }

  async ensureDevice(deviceDescriptor) {
    if (this.device && (!deviceDescriptor || this.device.id === deviceDescriptor.id)) {
      return this.device;
    }

    if (deviceDescriptor?.bluetoothDevice) {
      this.device = deviceDescriptor.bluetoothDevice;
      return this.device;
    }

    if (!hasWebBluetoothSupport()) {
      throw new Error('Web Bluetooth API is not available in this browser.');
    }

    if (typeof navigator.bluetooth.getDevices === 'function') {
      const devices = await navigator.bluetooth.getDevices();
      const match = devices.find((entry) => entry.id === deviceDescriptor?.id);
      if (match) {
        this.device = match;
        return this.device;
      }
    }

    throw new Error('Unable to access the selected Bluetooth device. Please scan again.');
  }

  async connect(deviceDescriptor, options = {}) {
    const device = await this.ensureDevice(deviceDescriptor);
    if (device.gatt?.connected) {
      return { success: true, device: this.createDescriptor(device) };
    }

    try {
      this.server = await device.gatt.connect();
      device.addEventListener('gattserverdisconnected', this.handleDisconnected);
    } catch (error) {
      throw new Error(error.message || 'Failed to connect to the printer.');
    }

    const serviceCandidates = options.serviceCandidates || SERVICE_CANDIDATES;
    const characteristicCandidates =
      options.characteristicCandidates || CHARACTERISTIC_CANDIDATES;

    let service = null;
    let characteristic = null;

    for (const serviceUuid of serviceCandidates) {
      try {
        service = await this.server.getPrimaryService(serviceUuid);
        this.selectedServiceUuid = serviceUuid;
      } catch (error) {
        service = null;
      }
      if (!service) continue;

      for (const characteristicUuid of characteristicCandidates) {
        try {
          characteristic = await service.getCharacteristic(characteristicUuid);
          this.selectedCharacteristicUuid = characteristicUuid;
          break;
        } catch (error) {
          characteristic = null;
        }
      }

      if (characteristic) {
        break;
      }
    }

    if (!service || !characteristic) {
      await this.disconnect();
      throw new Error('Unable to locate a writable characteristic on the printer.');
    }

    this.outputCharacteristic = characteristic;

    return {
      success: true,
      device: this.createDescriptor(device),
      serviceUuid: this.selectedServiceUuid,
      characteristicUuid: this.selectedCharacteristicUuid,
    };
  }

  async disconnect() {
    if (this.device) {
      this.device.removeEventListener('gattserverdisconnected', this.handleDisconnected);
    }

    if (this.device?.gatt?.connected) {
      try {
        this.device.gatt.disconnect();
      } catch (error) {
        return {
          success: false,
          message: error.message || 'Failed to disconnect printer.',
        };
      }
    }

    this.server = null;
    this.outputCharacteristic = null;
    this.selectedServiceUuid = null;
    this.selectedCharacteristicUuid = null;

    return { success: true };
  }

  async print(data, options = {}) {
    if (!this.outputCharacteristic) {
      throw new Error('Printer characteristic is not available. Please connect again.');
    }

    const chunkSize = options.chunkSize || BLE_WRITE_CHUNK_SIZE;
    const delayBetweenChunks = options.chunkDelay ?? 20;

    const payload =
      data instanceof Uint8Array ? data : Uint8Array.from(data || []);

    for (let offset = 0; offset < payload.length; offset += chunkSize) {
      const chunk = payload.slice(offset, offset + chunkSize);
      try {
        if (this.outputCharacteristic.writeValueWithoutResponse) {
          await this.outputCharacteristic.writeValueWithoutResponse(chunk);
        } else {
          await this.outputCharacteristic.writeValue(chunk);
        }
      } catch (error) {
        throw new Error(error.message || 'Failed to send data to printer.');
      }

      if (delayBetweenChunks > 0) {
        // eslint-disable-next-line no-await-in-loop
        await wait(delayBetweenChunks);
      }
    }

    return { success: true };
  }

  async getStatus() {
    return {
      connected: !!this.device?.gatt?.connected,
      device: this.device ? this.createDescriptor(this.device) : null,
      serviceUuid: this.selectedServiceUuid,
      characteristicUuid: this.selectedCharacteristicUuid,
    };
  }

  async restoreConnection(persistedState = {}) {
    if (!persistedState?.device?.id) {
      return { success: false, connected: false };
    }

    try {
      const device = await this.ensureDevice({ id: persistedState.device.id });
      if (!device) {
        return { success: false, connected: false };
      }
      const result = await this.connect({ id: device.id, bluetoothDevice: device });
      return { ...result, connected: result.success };
    } catch (error) {
      return {
        success: false,
        connected: false,
        message: error.message || 'Failed to restore Bluetooth connection.',
      };
    }
  }

  handleDisconnected() {
    this.server = null;
    this.outputCharacteristic = null;
    this.selectedServiceUuid = null;
    this.selectedCharacteristicUuid = null;
  }
}

